import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Coins, Upload, ArrowDownToLine, ImagePlus, ShieldCheck, Clock, XCircle, CheckCircle2, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal, { SectionLabel } from "../components/Reveal";

const inputCls =
  "mt-2 w-full rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none transition-colors focus:border-signal";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-muted";

const statusChip = (s) => {
  const map = {
    pending: { icon: Clock, cls: "border-gold/40 bg-gold/10 text-gold" },
    approved: { icon: CheckCircle2, cls: "border-profit/40 bg-profit/10 text-profit" },
    paid: { icon: CheckCircle2, cls: "border-profit/40 bg-profit/10 text-profit" },
    rejected: { icon: XCircle, cls: "border-loss/40 bg-loss/10 text-loss" },
  };
  const m = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] font-600 uppercase tracking-[0.12em] ${m.cls}`}>
      <m.icon size={10} /> {s}
    </span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Wallet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [firms, setFirms] = useState([]);
  const fileRef = useRef(null);

  const [claim, setClaim] = useState({ firm_slug: "", account_size: "", order_ref: "", purchase_date: "" });
  const [claimFile, setClaimFile] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);

  const [wd, setWd] = useState({ amount_coins: "", method: "upi", payout_detail: "" });
  const [wdBusy, setWdBusy] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/auth?next=/wallet", { replace: true });
  }, [user, navigate]);

  const load = () => {
    api.get("/coins/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load();
    api.get("/firms").then(({ data }) => setFirms(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const submitClaim = async () => {
    if (!claim.firm_slug || !claim.account_size.trim()) {
      toast.error("PICK A FIRM AND ACCOUNT SIZE");
      return;
    }
    if (!claim.purchase_date) {
      toast.error("ENTER THE PURCHASE DATE");
      return;
    }
    if (!claimFile) {
      toast.error("ATTACH YOUR ORDER SCREENSHOT");
      return;
    }
    setClaimBusy(true);
    try {
      const fd = new FormData();
      fd.append("firm_slug", claim.firm_slug);
      fd.append("account_size", claim.account_size);
      fd.append("order_ref", claim.order_ref);
      fd.append("purchase_date", claim.purchase_date);
      fd.append("file", claimFile);
      await api.post("/coins/claims", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClaim({ firm_slug: "", account_size: "", order_ref: "", purchase_date: "" });
      setClaimFile(null);
      toast.success("CLAIM SUBMITTED — THE DESK WILL VERIFY IT");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setClaimBusy(false);
    }
  };

  const submitWithdrawal = async () => {
    const amount = parseInt(wd.amount_coins, 10);
    if (!amount || amount < (wallet?.min_withdrawal || 10)) {
      toast.error(`MINIMUM WITHDRAWAL IS ${wallet?.min_withdrawal || 10} COINS`);
      return;
    }
    if (!wd.payout_detail.trim()) {
      toast.error("ENTER YOUR PAYOUT DETAILS");
      return;
    }
    setWdBusy(true);
    try {
      await api.post("/coins/withdraw", {
        amount_coins: amount,
        method: wd.method,
        payout_detail: wd.payout_detail,
      });
      setWd({ amount_coins: "", method: "upi", payout_detail: "" });
      toast.success("WITHDRAWAL REQUESTED — PAID WITHIN 48H AFTER REVIEW");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setWdBusy(false);
    }
  };

  return (
    <div data-testid="wallet-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="scanlines-overlay" />
        <div className="absolute -left-24 top-0 h-64 w-64 rounded-full bg-[#8B5CF6]/15 blur-[130px]" />
        <div className="relative mx-auto max-w-5xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Member Rewards</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
              FTC <span className="text-gradient">wallet</span>
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted">
              Buy any account through our links, claim it here, and earn FTC coins once the desk verifies your order. 1 coin = $1 — cash out via UPI or crypto from {wallet?.min_withdrawal || 10} coins.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3" data-testid="wallet-stats">
          <div className="card-violet p-6">
            <p className={labelCls}>Balance</p>
            <p className="mt-2 flex items-center gap-2 font-display text-4xl font-800 text-gold" data-testid="wallet-balance">
              <Coins size={26} /> {wallet ? wallet.balance : "…"}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">= ${wallet ? wallet.balance : "…"} USD</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted" data-testid="wallet-split">
              Withdrawable <span className="text-profit">{wallet ? wallet.withdrawable : "…"}</span>
              {" · "}Account credit <span className="text-gold">{wallet ? wallet.referral_credit : "…"}</span>
            </p>
          </div>
          <div className="card-violet p-6">
            <p className={labelCls}>Claims</p>
            <p className="mt-2 font-display text-4xl font-800 text-white" data-testid="wallet-claims-count">{wallet ? wallet.claims.length : "…"}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
              {wallet ? wallet.claims.filter((c) => c.status === "pending").length : 0} under review
            </p>
          </div>
          <div className="card-violet p-6">
            <p className={labelCls}>Withdrawn</p>
            <p className="mt-2 font-display text-4xl font-800 text-profit" data-testid="wallet-paid-out">
              {wallet ? wallet.withdrawals.filter((w) => w.status === "paid").reduce((s, w) => s + w.amount_coins, 0) : "…"}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">coins paid out</p>
          </div>
        </div>

        <Link to="/earn" data-testid="wallet-earn-link"
          className="card-violet mt-4 flex items-center justify-between gap-4 p-5 transition-colors duration-200 hover:border-gold/50">
          <span className="flex items-center gap-3">
            <Gift size={18} className="text-gold" />
            <span>
              <span className="block font-display text-sm font-700 text-white">Refer & Earn — invite friends, both get 0.15 coin</span>
              <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                Referral coins are account credit for buying firm accounts
              </span>
            </span>
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-signal">Open →</span>
        </Link>

        <div className="mt-10 grid items-start gap-6 lg:grid-cols-2">
          <div className="card-violet p-6 sm:p-7" data-testid="claim-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Claim Coins"}</p>
            <h2 className="mt-2 font-display text-xl font-800 text-white">Bought an account?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Submit your order details — once the desk verifies the purchase, coins land in your balance.
            </p>
            <div className="mt-5">
              <label className={labelCls}>Firm</label>
              <select value={claim.firm_slug} onChange={(e) => setClaim({ ...claim, firm_slug: e.target.value })}
                data-testid="claim-firm-select" className={inputCls}>
                <option value="">Select firm…</option>
                {firms.map((f) => <option key={f.slug} value={f.slug}>{f.name}</option>)}
              </select>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Account size</label>
                <input value={claim.account_size} onChange={(e) => setClaim({ ...claim, account_size: e.target.value })}
                  data-testid="claim-size-input" placeholder="$100K" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Order ref (optional)</label>
                <input value={claim.order_ref} onChange={(e) => setClaim({ ...claim, order_ref: e.target.value })}
                  data-testid="claim-order-input" placeholder="#A1B2C3" className={inputCls} />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Date of purchase</label>
              <input type="date" value={claim.purchase_date} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setClaim({ ...claim, purchase_date: e.target.value })}
                data-testid="claim-date-input" className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <div className="mt-4">
              <label className={labelCls}>Order screenshot</label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                data-testid="claim-file-input" onChange={(e) => setClaimFile(e.target.files?.[0] || null)} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="claim-choose-btn"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-ink px-4 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-muted transition-colors hover:border-signal/60 hover:text-signal">
                <ImagePlus size={14} /> {claimFile ? claimFile.name : "Attach order screenshot"}
              </button>
            </div>
            <button onClick={submitClaim} disabled={claimBusy} data-testid="claim-submit-btn"
              className="btn-beam btn-gradient mt-5 inline-flex w-full items-center justify-center gap-2 px-5 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-white disabled:opacity-50">
              <Upload size={13} /> {claimBusy ? "Submitting…" : "Submit Claim"}
            </button>
          </div>

          <div className="card-violet p-6 sm:p-7" data-testid="withdraw-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Cash Out"}</p>
            <h2 className="mt-2 font-display text-xl font-800 text-white">Withdraw coins</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Minimum {wallet?.min_withdrawal || 10} coins (${wallet?.min_withdrawal || 10}). The desk reviews and pays manually via UPI or crypto.
              Referral coins can't be withdrawn — they are account credit, spend them on the Refer & Earn page.
            </p>
            <div className="mt-5">
              <label className={labelCls}>Amount (coins)</label>
              <input value={wd.amount_coins} onChange={(e) => setWd({ ...wd, amount_coins: e.target.value.replace(/[^0-9]/g, "") })}
                data-testid="withdraw-amount-input" placeholder="10" inputMode="numeric" className={inputCls} />
            </div>
            <div className="mt-4">
              <label className={labelCls}>Method</label>
              <select value={wd.method} onChange={(e) => setWd({ ...wd, method: e.target.value, payout_detail: "" })}
                data-testid="withdraw-method-select" className={inputCls}>
                <option value="upi">UPI</option>
                <option value="crypto">Crypto</option>
              </select>
            </div>
            <div className="mt-4">
              <label className={labelCls}>{wd.method === "upi" ? "UPI ID" : "Crypto address + network"}</label>
              <input value={wd.payout_detail} onChange={(e) => setWd({ ...wd, payout_detail: e.target.value })}
                data-testid="withdraw-detail-input"
                placeholder={wd.method === "upi" ? "yourname@okhdfc" : "USDT (TRC20) — TX7…"} className={inputCls} />
            </div>
            <button onClick={submitWithdrawal} disabled={wdBusy} data-testid="withdraw-submit-btn"
              className="btn-beam btn-gradient-blue mt-5 inline-flex w-full items-center justify-center gap-2 px-5 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] disabled:opacity-50">
              <ArrowDownToLine size={13} /> {wdBusy ? "Requesting…" : "Request Withdrawal"}
            </button>
          </div>
        </div>

        <div className="mt-12 grid items-start gap-6 lg:grid-cols-2">
          <div className="card-violet p-6 sm:p-7" data-testid="claims-history">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// My Claims"}</p>
            <div className="mt-4 space-y-3">
              {!wallet ? null : wallet.claims.length === 0 ? (
                <p className="font-mono text-xs text-muted">No claims yet — buy through a firm page and claim it above.</p>
              ) : (
                wallet.claims.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-ink px-4 py-3.5" data-testid={`claim-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-600 text-white">{c.firm_name} · {c.account_size}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-muted">{fmtDate(c.created_at)}{c.purchase_date ? ` · bought ${c.purchase_date}` : ""}{c.order_ref ? ` · ${c.order_ref}` : ""}</p>
                      {c.admin_note && <p className="mt-1 font-mono text-[10px] text-loss">{c.admin_note}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      {statusChip(c.status)}
                      {c.status === "approved" && (
                        <span className="font-mono text-[10px] font-700 text-gold">+{c.coins} coins</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card-violet p-6 sm:p-7" data-testid="withdrawals-history">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Withdrawals"}</p>
            <div className="mt-4 space-y-3">
              {!wallet ? null : wallet.withdrawals.length === 0 ? (
                <p className="font-mono text-xs text-muted">No withdrawals yet.</p>
              ) : (
                wallet.withdrawals.map((w) => (
                  <div key={w.id} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-ink px-4 py-3.5" data-testid={`withdrawal-row-${w.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-600 text-white">{w.amount_coins} coins · {w.method.toUpperCase()}</p>
                      <p className="mt-0.5 truncate font-mono text-[10px] text-muted">{fmtDate(w.created_at)} · {w.payout_detail}</p>
                      {w.admin_note && <p className="mt-1 font-mono text-[10px] text-loss">{w.admin_note}</p>}
                    </div>
                    {statusChip(w.status)}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="card-violet mt-6 p-6 sm:p-7" data-testid="ledger-history">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Coin Ledger"}</p>
          <div className="mt-4 space-y-2">
            {!wallet ? null : wallet.ledger.length === 0 ? (
              <p className="font-mono text-xs text-muted">No coin movements yet.</p>
            ) : (
              wallet.ledger.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 border-b border-line/50 pb-2 font-mono text-xs" data-testid={`ledger-row-${l.id}`}>
                  <span className="text-muted">{l.note || l.kind}</span>
                  <span className="flex items-center gap-4">
                    <span className="text-muted">{fmtDate(l.created_at)}</span>
                    <span className={`font-700 ${l.delta >= 0 ? "text-profit" : "text-loss"}`}>{l.delta >= 0 ? "+" : ""}{l.delta}</span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <p className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
          <ShieldCheck size={12} className="text-profit" /> Coins are credited by the desk only after order verification — abuse gets accounts banned.
        </p>
      </section>
      <Footer />
    </div>
  );
}
